import type { TaskStatus } from './status'

/**
 * **Der Fokus-Puls** ([Z-22](../../../docs/phasen/zusatz-22-fokus-puls.md),
 * [E-416](../../../docs/ENTSCHEIDUNGEN.md#e-416)).
 *
 * Nils, 19.10.2026: *„den circle müssen wir noch ein bisschen optimieren das er wie so ein FOCUS
 * PULSE ist, also mit timer das man sagen kann – jetzt arbeite ich an dem ding, oder an den 2
 * dingen – das muss im fokus stehen."*
 *
 * ## Warum „Puls" und nicht „Fokus"
 *
 * „Fokus" ist in Work schon vergeben: **„Fokus voll (3 von 3)"** heißt, dass die WIP-Plätze
 * belegt sind. Das hier ist etwas anderes – nicht *was in Arbeit ist*, sondern *woran ich in
 * diesem Moment sitze*, höchstens zwei Dinge, mit einer Uhr. Ein eigenes Wort verhindert, dass
 * „Fokus beenden" so klingt, als würde man Aufgaben aus „In Arbeit" nehmen.
 *
 * ## Was hier steht
 *
 * Nur Rechnung, kein Zustand: Server und Browser rechnen mit denselben Funktionen, und „jetzt"
 * wird immer **übergeben**, nie gelesen – sonst ließe sich die Uhr nicht testen.
 */

/** Höchstens zwei Dinge im Puls: eins ist dran, das andere ruht. */
export const PULS_PLAETZE = 2

/** Die letzten fünf Minuten: der Puls schlägt schneller und wird warm. */
export const PULS_ENDSPURT_MS = 5 * 60_000

/** Worauf die Uhr zählt. `termin` = bis zum nächsten Termin aus dem Kalender. */
export const PULS_UHREN = ['termin', '25', '50', 'offen'] as const
export type PulsUhr = (typeof PULS_UHREN)[number]

export const PULS_UHR_LABELS: Record<PulsUhr, string> = {
  termin: 'bis Termin',
  '25': '25 min',
  '50': '50 min',
  offen: 'offen',
}

/** Der Stand eines laufenden Pulses, wie ihn der Server speichert und ausliefert. */
export interface PulsStand {
  aktivId: string
  ruhendId: string | null
  uhr: PulsUhr
  /** ISO-Zeitpunkt, an dem der Block begann (bei „Uhr stellen" neu gesetzt). */
  beginn: string
  /** Nur bei `termin`: Beginn des Termins, auf den gezählt wird. */
  zielAm: string | null
  zielTitel: string | null
  pausiertSeit: string | null
  /** Bereits abgeschlossene Pausen des Blocks. */
  pausiertMs: number
  /** Summe aller „+15 min". */
  extraMs: number
}

export interface PulsZeit {
  /** Gelaufene Zeit des Blocks ohne Pausen. */
  laufzeitMs: number
  /** Was bis zum Ende bleibt – negativ, wenn überzogen; `null` bei offener Uhr. */
  restMs: number | null
  /** Anteil für den Bogen, 0–1. Bei offener Uhr eine Runde pro Stunde. */
  anteil: number
  ueberzogen: boolean
  endspurt: boolean
  pausiert: boolean
}

const ms = (iso: string) => new Date(iso).getTime()

/**
 * **Wo die Uhr steht.**
 *
 * Bei `termin` zählt die Uhr auf einen **festen Zeitpunkt** – eine Pause verschiebt den Termin
 * nicht. Bei 25 und 50 Minuten ist es ein Block Arbeitszeit, dort verlängert eine Pause das Ende.
 */
export function pulsZeit(s: PulsStand, jetzt: number): PulsZeit {
  const beginn = ms(s.beginn)
  const pausiert = s.pausiertSeit !== null
  const laufendePause = pausiert ? Math.max(0, jetzt - ms(s.pausiertSeit!)) : 0
  const laufzeitMs = Math.max(0, jetzt - beginn - s.pausiertMs - laufendePause)

  if (s.uhr === 'offen') {
    return {
      laufzeitMs,
      restMs: null,
      anteil: (laufzeitMs % 3_600_000) / 3_600_000,
      ueberzogen: false,
      endspurt: false,
      pausiert,
    }
  }

  let restMs: number
  let anteil: number
  if (s.uhr === 'termin' && s.zielAm) {
    const ende = ms(s.zielAm) + s.extraMs
    restMs = ende - jetzt
    anteil = (jetzt - beginn) / Math.max(1, ende - beginn)
  } else {
    const dauer = (s.uhr === '25' ? 25 : 50) * 60_000 + s.extraMs
    restMs = dauer - laufzeitMs
    anteil = laufzeitMs / dauer
  }
  return {
    laufzeitMs,
    restMs,
    anteil: Math.min(1, Math.max(0, anteil)),
    ueberzogen: restMs < 0,
    endspurt: restMs < PULS_ENDSPURT_MS,
    pausiert,
  }
}

export interface PulsTermin {
  beginn: string
  ende: string
  betreff: string
  ganztaegig: boolean
  /** Outlook: `free` blockiert nichts und ist deshalb kein Ziel für die Uhr. */
  zeigtAls?: string | null
}

/**
 * **Der nächste Termin, auf den die Uhr zählen kann** – der erste, der nach `jetzt` beginnt.
 * Ganztägiges und als „frei" Eingetragenes zählt nicht: Ein Maler im Haus beendet keinen Block.
 */
export function naechsterPulsTermin(
  termine: readonly PulsTermin[],
  jetzt: number,
): PulsTermin | null {
  let bester: PulsTermin | null = null
  for (const t of termine) {
    if (t.ganztaegig || t.zeigtAls === 'free') continue
    const b = ms(t.beginn)
    if (!(b > jetzt)) continue
    if (!bester || b < ms(bester.beginn)) bester = t
  }
  return bester
}

/**
 * **Das Ziel einer Uhr.** Ohne Termin fällt „bis Termin" auf 50 Minuten zurück, statt eine Uhr
 * ohne Ende anzuzeigen, die so tut, als zähle sie auf etwas.
 */
export function pulsUhrZiel(
  uhr: PulsUhr,
  termine: readonly PulsTermin[],
  jetzt: number,
): { uhr: PulsUhr; zielAm: string | null; zielTitel: string | null } {
  if (uhr !== 'termin') return { uhr, zielAm: null, zielTitel: null }
  const t = naechsterPulsTermin(termine, jetzt)
  return t
    ? { uhr, zielAm: t.beginn, zielTitel: t.betreff }
    : { uhr: '50', zielAm: null, zielTitel: null }
}

/** `12:05` oder `1:12:05` – für die große Zahl und den Tab-Titel. */
export function pulsUhrzeit(msWert: number): string {
  const s = Math.floor(Math.max(0, msWert) / 1000)
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const zwei = (n: number) => String(n).padStart(2, '0')
  return h > 0 ? `${h}:${zwei(m)}:${zwei(s % 60)}` : `${zwei(m)}:${zwei(s % 60)}`
}

/** `< 1 min`, `12 min`, `1 h 05` – für Summen. */
export function pulsDauer(msWert: number): string {
  const m = Math.floor(Math.max(0, msWert) / 60_000)
  if (m < 1) return '< 1 min'
  return m < 60 ? `${m} min` : `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, '0')}`
}

export interface PulsAbschnitt {
  taskId: string
  von: string
  /** `null` = läuft gerade. */
  bis: string | null
}

/** **Wie lange heute woran** – ein offener Abschnitt zählt bis `jetzt`. */
export function pulsSummen(
  abschnitte: readonly PulsAbschnitt[],
  jetzt: number,
): Map<string, number> {
  const summe = new Map<string, number>()
  for (const a of abschnitte) {
    const dauer = Math.max(0, (a.bis ? ms(a.bis) : jetzt) - ms(a.von))
    summe.set(a.taskId, (summe.get(a.taskId) ?? 0) + dauer)
  }
  return summe
}

/** Ein neuer Block: worauf die Uhr ab jetzt zählt (der Browser kennt die Termine, der Server nicht). */
export interface PulsBlock {
  uhr: PulsUhr
  zielAm: string | null
  zielTitel: string | null
}

/**
 * **Was man mit dem Puls tun kann.** Erledigt ist keine eigene Aktion: Der Status geht über den
 * üblichen Weg (`tasks.statusSetzen`), der Puls lässt die Aufgabe danach nur los (`verlassen`).
 */
export type PulsAktion =
  | ({ art: 'holen'; taskId: string; dran?: boolean } & PulsBlock)
  | { art: 'wechseln' }
  | { art: 'pausieren' }
  | { art: 'weiter' }
  | { art: 'verlaengern'; ms: number }
  | ({ art: 'uhrStellen' } & PulsBlock)
  | { art: 'verlassen'; taskId: string; neuerBlock?: PulsBlock | null }
  | { art: 'beenden' }

const neuerBlock = (b: PulsBlock, jetzt: string) => ({
  uhr: b.uhr,
  zielAm: b.uhr === 'termin' ? b.zielAm : null,
  zielTitel: b.uhr === 'termin' ? b.zielTitel : null,
  beginn: jetzt,
  pausiertMs: 0,
  extraMs: 0,
})

/**
 * **Der nächste Stand** – rein, für Server und Browser. `null` heißt: kein Puls.
 *
 * - **holen** ohne Puls beginnt einen. Mit Puls kommt das Neue als zweites Ding dazu und ruht;
 *   war schon eins da, fällt das ruhende heraus (es bleibt in Arbeit, nur nicht mehr im Puls).
 *   Mit `dran` ist das Neue sofort dran und das bisherige ruht.
 * - **verlassen** des Aktiven macht das Ruhende zum Aktiven; ohne Ruhendes endet der Puls.
 *   Kommt ein `neuerBlock` mit (die Uhr war abgelaufen), beginnt die Uhr für das Nachrückende neu.
 */
export function pulsSchritt(s: PulsStand | null, a: PulsAktion, jetzt: string): PulsStand | null {
  const t = new Date(jetzt).getTime()
  switch (a.art) {
    case 'holen': {
      if (!s) {
        return { aktivId: a.taskId, ruhendId: null, pausiertSeit: null, ...neuerBlock(a, jetzt) }
      }
      if (s.aktivId === a.taskId) return s
      if (a.dran) return { ...s, aktivId: a.taskId, ruhendId: s.aktivId }
      return { ...s, ruhendId: a.taskId }
    }
    case 'wechseln':
      return s && s.ruhendId ? { ...s, aktivId: s.ruhendId, ruhendId: s.aktivId } : s
    case 'pausieren':
      return s && !s.pausiertSeit ? { ...s, pausiertSeit: jetzt } : s
    case 'weiter':
      return s && s.pausiertSeit
        ? {
            ...s,
            pausiertSeit: null,
            pausiertMs: s.pausiertMs + Math.max(0, t - new Date(s.pausiertSeit).getTime()),
          }
        : s
    case 'verlaengern':
      return s ? { ...s, extraMs: s.extraMs + a.ms } : s
    case 'uhrStellen':
      return s ? { ...s, pausiertSeit: null, ...neuerBlock(a, jetzt) } : s
    case 'verlassen': {
      if (!s) return s
      if (s.ruhendId === a.taskId) return { ...s, ruhendId: null }
      if (s.aktivId !== a.taskId) return s
      if (!s.ruhendId) return null
      const weiter = { ...s, aktivId: s.ruhendId, ruhendId: null }
      return a.neuerBlock
        ? { ...weiter, pausiertSeit: null, ...neuerBlock(a.neuerBlock, jetzt) }
        : weiter
    }
    case 'beenden':
      return null
  }
}

/**
 * **Wann der Puls eine Aufgabe von selbst loslässt** (Nils, 20.10.2026: *„hatte nämlich jetzt
 * die aufgabe auf erledigt gesetzt, der lief dann aber noch weiter"*).
 *
 * Dieselben zwei Ausgänge wie der grüne Knopf im Puls (`abschluss`): erledigt oder zur Abnahme
 * gegeben. Wartet die Aufgabe nur oder ist sie zurückgestellt, bleibt sie im Puls – das ist
 * eine Unterbrechung, kein Ende, und „↷ Später" bzw. „Puls beenden" stehen dafür bereit.
 */
export const PULS_LOSLASSEN_BEI: readonly TaskStatus[] = ['erledigt', 'review']

/**
 * **Der Stand ohne Abgeschlossenes** – egal, auf welchem Weg der Status gesetzt wurde
 * (Aufgabendetail, Stapel, GitLab, NetCon). `statusVon` liefert den wirksamen Status oder
 * `undefined`, wenn er unbekannt ist; Unbekanntes bleibt im Puls.
 */
export function pulsOhneAbgeschlossene(
  s: PulsStand | null,
  statusVon: (taskId: string) => TaskStatus | null | undefined,
  jetzt: string,
): PulsStand | null {
  const fertig = (id: string | null) => {
    const st = id ? statusVon(id) : undefined
    return !!st && PULS_LOSLASSEN_BEI.includes(st)
  }
  let neu = s
  if (neu?.ruhendId && fertig(neu.ruhendId)) {
    neu = pulsSchritt(neu, { art: 'verlassen', taskId: neu.ruhendId }, jetzt)
  }
  if (neu && fertig(neu.aktivId)) {
    neu = pulsSchritt(neu, { art: 'verlassen', taskId: neu.aktivId }, jetzt)
  }
  return neu
}

/** Woran die Uhr gerade zählt – `null`, wenn kein Puls läuft oder er pausiert. */
export const pulsLaeuftAn = (s: PulsStand | null): string | null =>
  s && !s.pausiertSeit ? s.aktivId : null

/**
 * **Die Abschnitte nachziehen**: Ändert sich, woran die Uhr zählt, endet der offene Abschnitt
 * und – wenn wieder etwas läuft – beginnt ein neuer. Gibt zurück, was zu schließen und was zu
 * öffnen ist; Server und Browser schreiben es jeweils in ihre eigene Ablage.
 */
export function pulsAbschnittWechsel(
  alt: PulsStand | null,
  neu: PulsStand | null,
): { schliessen: boolean; oeffnen: string | null } {
  const vorher = pulsLaeuftAn(alt)
  const nachher = pulsLaeuftAn(neu)
  if (vorher === nachher) return { schliessen: false, oeffnen: null }
  return { schliessen: vorher !== null, oeffnen: nachher }
}
